/**
 * Liquidity Queue API Service
 * Lists, restarts and cancels transactions queued for company liquidity
 */

import axiosInstance from '@/lib/axios'
import API_ROUTES from '@/lib/api-routes'
import type {
    QueuedTransactionsResponse,
    QueuedTransactionActionResponse,
} from '@/types/liquidity-queue'

/** The API wraps every handler result as `{ status, statusCode, message, data }`. */
interface ApiEnvelope<T> {
    status: string
    statusCode: number
    message: string
    data: T
}

/**
 * GET /admin/liquidity/failed/list
 */
export const getQueuedTransactions = async (
    page = 1,
    limit = 20
): Promise<QueuedTransactionsResponse> => {
    const response = await axiosInstance.get<
        ApiEnvelope<QueuedTransactionsResponse>
    >(API_ROUTES.LIQUIDITY_QUEUE.LIST, { params: { page, limit } })
    return response.data.data
}

/**
 * Re-runs the transaction if company liquidity now covers it.
 * POST /admin/liquidity/failed/{id}/restart
 */
export const restartQueuedTransaction = async (
    id: string
): Promise<QueuedTransactionActionResponse> => {
    const response = await axiosInstance.post<
        ApiEnvelope<QueuedTransactionActionResponse['data']>
    >(API_ROUTES.LIQUIDITY_QUEUE.RESTART(id))
    // The interceptor lifts the handler's message onto the envelope.
    return { message: response.data.message, data: response.data.data }
}

/**
 * Cancels the transaction and returns the held funds to the user.
 * POST /admin/liquidity/failed/{id}/cancel
 */
export const cancelQueuedTransaction = async (
    id: string
): Promise<QueuedTransactionActionResponse> => {
    const response = await axiosInstance.post<
        ApiEnvelope<QueuedTransactionActionResponse['data']>
    >(API_ROUTES.LIQUIDITY_QUEUE.CANCEL(id))
    // The interceptor lifts the handler's message onto the envelope.
    return { message: response.data.message, data: response.data.data }
}
